import { useEffect, useRef, useState } from "react"
import { useRouter, usePathname } from '@/platform/navigation'
import { useTranslations } from '@/lib/messages'
import { useAuth } from "@/lib/auth-context"
import { firstAllowedRoute, type AppModule } from "@/lib/modules"
import { SETTINGS_HUB_PATH, canAccessSettingsHub } from "@/lib/settings-nav"
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar"
import { Separator } from "@/components/ui/separator"
import { AppSidebar } from "@/components/app-sidebar"
import { TrialBanner } from "@/components/trial-banner"
import { SessionRevokedModal } from "@/components/auth/session-revoked-modal"
import { ShopLocalBranchDialog } from "@/components/shop-local-branch-dialog"
import { usePageUnloadGuard } from "@/hooks/use-page-unload-guard"
import { UnsavedChangesProvider } from "@/lib/unsaved-changes-context"
import { cn } from "@/lib/utils"
import { activeBranchLabel } from "@/lib/branch-selection"
import { BranchWorkflowNotice } from "@/components/branch-workflow-notice"
import { WorkspaceNotificationsProvider } from "@/lib/workspace-notifications-context"


const FULL_WIDTH_PATHS = [
  "/dashboard/pos",
  "/dashboard/restaurant/pos",
  "/dashboard/purchasing",
  "/dashboard/sales/invoice",
  "/dashboard/sales/payments",
  "/dashboard/purchasing/payments",
  "/dashboard/purchasing/purchases",
]
const FULL_WIDTH_EXACT_PATHS = ["/dashboard/branches"]

const WIDE_CONTENT_PATHS = ["/dashboard/reports", "/dashboard/executive-reports", "/dashboard/settings", "/dashboard/stock"]
/** Exact hubs only — do not use startsWith (would widen nested invoice/payment routes). */
const WIDE_HUB_EXACT_PATHS = ["/dashboard", "/dashboard/sales"]

function moduleForPath(pathname: string): AppModule | null {
  if (pathname.startsWith("/dashboard/branches")) return null
  if (pathname.startsWith("/dashboard/notifications")) return null
  if (pathname.startsWith("/dashboard/super-admin")) return null
  // User management is granted by the tenant role, independent of subscription app modules.
  // The admin page applies the tenant role check; Super Admin remains in admin-frontend.
  if (pathname.startsWith("/dashboard/admin")) return null
  if (pathname.startsWith("/dashboard/restaurant")) return "restaurant"
  if (
    pathname.startsWith("/dashboard/pos/tables") ||
    pathname.startsWith("/dashboard/pos/kds") ||
    pathname.startsWith("/dashboard/pos/orders") ||
    pathname.startsWith("/dashboard/pos/history")
  ) {
    return "restaurant"
  }
  if (pathname.startsWith("/dashboard/pos")) return "sales"
  if (pathname.startsWith("/dashboard/sales")) return "sales"
  if (pathname.startsWith("/dashboard/stock")) return "stock"
  if (pathname.startsWith("/dashboard/purchasing")) return "purchasing"
  if (pathname.startsWith("/dashboard/executive-reports")) return "reports"
  if (pathname.startsWith("/dashboard/reports")) return "reports"
  return "dashboard"
}

const AUTO_CLOSE_PATHS = [
  "/dashboard/pos",
  "/dashboard/restaurant/pos",
  "/dashboard/sales/invoice",
  "/dashboard/purchasing/purchases",
]

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const router = useRouter()
  const pathname = usePathname()
  const { user, loading, hasModule, backendUnavailable } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [showSpinner, setShowSpinner] = useState(false)
  const wasAutoClosed = useRef(false)
  const tCommon = useTranslations("common")

  usePageUnloadGuard()


  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => setShowSpinner(true), 150)
      return () => clearTimeout(timer)
    } else {
      setShowSpinner(false)
    }
  }, [loading])

  useEffect(() => {
    const isAutoClose =
      AUTO_CLOSE_PATHS.some((p) => pathname.startsWith(p)) ||
      pathname === "/dashboard/branches"
    if (isAutoClose) {
      wasAutoClosed.current = true
      setSidebarOpen(false)
    } else if (wasAutoClosed.current) {
      wasAutoClosed.current = false
      setSidebarOpen(true)
    }
  }, [pathname])

  useEffect(() => {
    if (!loading && !user && !backendUnavailable) {
      router.push("/login")
      return
    }
    if (!loading && user) {
      // Subdomain redirect disabled: tenants stay on the root domain (ibtpro.com).
      // if (user.tenantSlug && user.systemRole !== "super_admin") {
      //   const currentHost = window.location.host.toLowerCase()
      //   const rootDomain = (import.meta.env.VITE_ROOT_DOMAIN || "ibtpro.com").toLowerCase().replace(/:\d+$/, "")
      //   const port = window.location.port ? `:${window.location.port}` : ""
      //   const protocol = window.location.protocol
      //
      //   if (currentHost === rootDomain || currentHost === `www.${rootDomain}`) {
      //     window.location.href = `${protocol}//${user.tenantSlug}.${rootDomain}${port}${pathname}`
      //     return
      //   }
      // }

      const required = moduleForPath(pathname)
      const salesHidden = user.features?.salesSidebar === false
      const purchaseHidden = user.features?.purchaseSidebar === false
      if (
        (required && !hasModule(required)) ||
        (salesHidden && pathname.startsWith("/dashboard/sales")) ||
        (purchaseHidden && pathname.startsWith("/dashboard/purchasing")) ||
        (pathname.startsWith(SETTINGS_HUB_PATH) && !canAccessSettingsHub(user.role))
      ) {
        router.replace(firstAllowedRoute(user.modules, user.features))
        return
      }
      if (
        pathname.startsWith("/dashboard/pos/touch") &&
        user.features?.touchPos !== true
      ) {
        router.replace("/dashboard/pos/register")
      }
    }
  }, [user, loading, backendUnavailable, pathname, hasModule, router])

  if (loading || !user) {
    if (!showSpinner) {
      return null
    }
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background">
        <div
          className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground/40 border-t-primary"
          aria-hidden
        />
        <p className="text-sm text-muted-foreground" role="status">
          {backendUnavailable ? tCommon("reconnecting") : tCommon("loading")}
        </p>
      </div>
    )
  }

  const isWorkflowShell =
    FULL_WIDTH_PATHS.some((p) => pathname.startsWith(p)) ||
    FULL_WIDTH_EXACT_PATHS.includes(pathname)
  const isWideContent =
    WIDE_CONTENT_PATHS.some((p) => pathname.startsWith(p)) ||
    WIDE_HUB_EXACT_PATHS.includes(pathname)
  /** Report screen/view: no sidebar, no top chrome — report only. */
  const isReportScreen =
    (pathname.includes("/dashboard/reports/") &&
      (pathname.endsWith("/view") || pathname.endsWith("/screen") || pathname.includes("/view/"))) ||
    (pathname.includes("/dashboard/executive-reports/") && pathname.endsWith("/view"))

  if (isReportScreen) {
    // Report view/screen: no sidebar, header, or trial banner — report chrome only.
    return (
      <UnsavedChangesProvider>
        <a href="#main-content" className="skip-to-content">
          {tCommon("skipToContent")}
        </a>
        <SessionRevokedModal />
        <div id="main-content" className="h-svh min-h-0 overflow-hidden bg-background">
          {children}
        </div>
      </UnsavedChangesProvider>
    )
  }

  return (
    <UnsavedChangesProvider>
      <WorkspaceNotificationsProvider>
      <SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <a href="#main-content" className="skip-to-content">
          {tCommon("skipToContent")}
        </a>
        <AppSidebar />
        <SidebarInset
          className={cn(
            "flex min-h-0 flex-col !bg-muted/20",
            isWorkflowShell ? "h-svh overflow-hidden" : "overflow-y-auto",
          )}
        >
          <header className="flex h-12 shrink-0 items-center gap-2 border-b bg-background px-3 md:px-4">
            <SidebarTrigger />
            <Separator orientation="vertical" className="h-6" />
            {activeBranchLabel() && <span className="truncate text-sm font-medium">{activeBranchLabel()?.name}</span>}
          </header>
          <BranchWorkflowNotice pathname={pathname} />
          <TrialBanner />
          <SessionRevokedModal />
          <ShopLocalBranchDialog />
          <div
            id="main-content"
            className={cn(
              "app-shell-content app-aspect-frame",
              (isWorkflowShell || isWideContent) && "app-shell-full-width",
              isWorkflowShell && "app-shell-workflow",
            )}
          >
            {children}
          </div>
        </SidebarInset>
      </SidebarProvider>
      </WorkspaceNotificationsProvider>
    </UnsavedChangesProvider>
  )
}
